/** @module Quality/TestRunForm */
define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/store/Memory",
	"csi",
	"csi/_base/modules",
	"csi/_base/underscore",
	"csi/comm",
	"csi/comm/PostRequest",
	"csi/def",
	"csi/def/Action",
	"csi/def/Field",
	"csi/def/Relationship",
	"csi/def/ViewRef",
	"csi/dgrid/MatrixEditor",
	"csi/html",
	"csi/locale",
	"csi/message",
	"csi/reflection",
	"csi/store/_base",
	"csi/store/Names",
	"csi/xml",
	"csi/xml/Query",
	"csi/xml/Search",
	"Apparel/ProductType",
	"SiteAdmin/DataSheetForm"
], function(declare, lang, Memory, csi, modules, _, comm, PostRequest, def, Action, Field, Relationship, ViewRef, MatrixEditor, html, locale, message,
	reflection, csiStore, Names, xml, Query, Search, ProductType, DataSheetForm) {

	var _SetupClass = declare(null, {
		runRequest: true,

		/**
		 * Function passed to csi.syncRequire to revise the data to initialize the form
		 * based on whether there is a testRunContextNode and retrieving related colors
		 * @param {Widget} form - parent form object
		 * @param {Controller} ac - Related controller object
		 * @param {Array} urls - string Array of URLs for the current request
		 * @param {Array} paths - string Array of paths for the current request
		 */
		request: function(form, ac, urls, paths) {
			// summary: add urls and paths needed for product
			const {testRunContextNode} = form;
			if (testRunContextNode) {
				urls.push(testRunContextNode.$URL);
				paths.push("Child:ProductColors;Attributes");
			}
		},

		/**
		 * Function passed to csi.syncRequire to initialize for setup
		 * @param {Widget} form - parent form object
		 * @param {Controller} ac - related controller object
		 */
		init: function(form, ac) {
			// summary: initialize colors and defaults based on Product
			var cache = form.cacheView;
			var colors = [];
			var sizes = [];
			var defaultSize = null;
			if (form.testRunContextNode && form.hasProductColors()) {
				colors = form.testRunContextNode.ProductColors;
			}
			var dimValues = form._setProductDimensions(colors, true);

			// specify default values if not already specified
			form.addDefaultValue(xml.ATTR_NAME, ac.m_formData.getFieldValue(xml.ATTR_NAME));

			// decide default value for dimensions
			var dimDefault = form.DIM_NONE;
			// if colors option is available, use that as default
			if (dimValues && _.contains(dimValues, form.DIM_COLORS)) {
				dimDefault = form.DIM_COLORS;
			}

			form.addDefaultValue(form.DIMENSIONS, dimDefault);

			return dimValues;
		}
	});

	return declare(DataSheetForm, {
		'class': 'csi-form-view-block',

		// field ids
		COLOR: "TestRunColor",
		COLORS: "DimensionColors",
		DIMENSIONS: "DimensionColorOption",
		QUANTITY: "DimensionQuantity",
		CUSTOM_QUANTITY: "DimensionMatrix",
		TEST_RUN: "TestRun",
		TEST_SPEC: "TestSpec",
		TEST_SPECS: "TestSpecs",
		TEST_SPEC_GROUP: "TestSpecGroup",
		GROUP_SPECS: "GroupSpecs",
		// dimension choices
		DIM_NONE: "0",
		DIM_COLORS: "1",
		DIM_PER_ACTIVE_COLOR: "2",

		/** @property {Boolean} - Flag specifying if form can be used to create multiple test runs */
		bCanCreateMultipleTestRuns: false,

		/** @property {Node} - parent product node, if any, for TestRun (null if creating Template) */
		testRunContextNode: null,

		/** @property {String[]} - colors displayed for selection */
		_colors: null,

		/** @property {String} - URL to Color if Test Run is tied to a colored material
		 * If the property is set to "centric:", this signifies that the user is forced to no
		 * available color selection even if colors are available.  This is useful in the case where TestRun is
		 * created from a Sample where no color is assigned and the color attribute for the TestRun needs to coincide
		 * with that of the related Sample.
		 *
		 * If set to null, user can select a color for the TestRun, if available.
		 */
		testRunColor: null,

		/** @property {String} - color field name (NOT labels) */
		colorsLabel: null,

		/** @property {String} - test specs field name (NOT labels) */
		testSpecsLabel: null,

		/** @property {Field[]} - dimension fields */
		_dimensionFields: null,

		/** @property {Object[]} - dimension items */
		_dimensionStore: null,

		/** @property {MatrixEditor} - custom quantity matrix editor */
		_matrix: null,

		/** @property {DOMNode} - row containing custom quantity matrix */
		_matrixRow: null,

		/** @property {Object} - class to use to initialize form */
		_productSetupClass: _SetupClass,

		/** @property {Object} - instance of _productSetupClass */
		_productSetup: null,

		/** @property {String[]} - URL Array of test runs being created */
		newTestRuns: null,

		/**
		 * Determines if colors are available for the current product
		 * @param {Node} product - Node objcet for product to check
		 * @return {Boolean} - true if there are colors available for the product, otherwise false
		 */
		hasProductColors: function() {
			return this.testRunContextNode && ProductType.hasColor(this.testRunContextNode) && this.testRunContextNode.ProductColors.length > 0;
		},

		/**
		 * @override
		 * Handle retrieving data for field stores
		 * @param {Field} field - field to retrieve related data store
		 * @param {String} value - initial value for the field
		 */
		getFieldStore: function(field, value) {
			var name = field.getControlName();
			if (name == this.DIMENSIONS) {
				return this._dimensionStore;
			}

			if (this.testRunContextNode && name == this.colorsLabel) {
				return this._getColorFieldStore();
			}

			if (name == this.testSpecsLabel) {
				return this._getTestSpecsStore();
			}

			return this.inherited(arguments);
		},

		/**
		 * Handle retrieving data for Color field/Matrix stores
		 * @param {String} value - initial value for the field
		 * @return {Store} - Store object for Color
		 */
		_getColorFieldStore: function(value) {
			var gotoDialog = "ProductColor";
			var params = {
				m_id: "ProductColors",
				m_url: this.testRunContextNode.$URL,
			};
			var q = new Query();
			if (xml.isValidURL(value)) {
				q.Node(xml.PROP_URL, xml.OP_EQUALS, value); // if color has got fixed value
			} else {
				q.Attr("Active", xml.OP_EQUALS, true);
			}

			params.m_query = q;

			var rel = lang.mixin(new Relationship(), params);
			var storeOptions = csiStore.namesOptionsByRelationship(this.getContext(), rel);
			storeOptions.action = lang.mixin(new Action(), {
				m_name: locale.resource("AggregateInline_Name", [locale.typePlural(gotoDialog)]),
				m_viewRef: lang.mixin(new ViewRef(), params),
				m_fixedQuery: params.m_query // possible extra filter
			});
			return new Names(storeOptions);
		},

		/**
		 * create store for TestSpecs
		 */
		_getTestSpecsStore: function() {
			const {TEST_SPEC, TEST_SPECS} = this;
			var search = new Search();
			var q = search.getQuery();

			q.Node(xml.PROP_TYPE, xml.OP_EQUALS, TEST_SPEC)
				.RefAttr("__Parent__", xml.OP_EQUALS, csi.EMPTY_URL)
				.Attr("State", xml.OP_EQUALS, "Revision.State:APPROVED", "Child:CurrentRevision");

			var params = {
				m_id: this.TEST_SPECS,
				m_url: "centric://QUALITY/Views"
			};

			var rel = lang.mixin(new Relationship(), {
				m_typeNode: this.TEST_SPEC,
				m_query: q
			});
			var storeOptions = csiStore.namesOptionsByRelationship(this.getContext(), rel);
			storeOptions.action = lang.mixin(new Action(), {
				m_name: locale.resource("AggregateInline_Name", [locale.typePlural(TEST_SPECS)]),
				m_viewRef: lang.mixin(new ViewRef(), params),
				m_fixedQuery: q
			});

			return new Names(storeOptions);
		},

		/**
		 * Support for Controller.toWriter() to be able to generate TestRuns according to form settings
		 * @param {Controller} ac - related Controller for write operation
		 */
		toWriterTestRuns: function(ac) {
			var fd = this.defView.m_formData;
			// Mark colors field as Used so that the value isn't set in toWriterFields below
			const colors = !this.hasProductColors() ? [] : fd.getFieldValue(this.colorsLabel, true) || [];
			var testRuns = [];

			var w = ac.m_writer;
			var prefix = fd.getFieldValue(xml.ATTR_NAME, true);
			var qty = fd.getFieldValue(this.QUANTITY, true) || 1; // default of 1
			var self = this;

			// use view to get field value in case field is hidden
			var dims = this.defView.getFieldValue(this.DIMENSIONS) || this.DIM_NONE; // default of NONE
			var nameField = this.defView.getField("Node Name");
			var nameHasExpr = (nameField && nameField.hasExpression());

			var createTestRuns = function(n, colorURL) {
				for (var i = 1; i <= n; ++i) {
					var url = comm.createUrl(self.TEST_RUN);
					ac.m_urlNode = url;

					// generate CallMethod for new TestRun URL
					ac.toWriterRequest(fd);

					// set fields
					ac.toWriterFields(url, fd);

					// Colors field is a reflist, but we need to set TestRunColor as a ref on each new TestRun
					w.changeNode(xml.getUrlPath(url, "Child:LatestRevision"))
						.changeAttributeFlag(self.COLOR, xml.ATTR_FLAG_MODIFIED_BY_CLIENT, true)
						.changeAttribute(self.COLOR, xml.ATTR_REF, colorURL || csi.EMPTY_URL); // if color is null, use dummy

					// see if there is an expression on Node Name set by the Business Object - if so, ignore this next part
					if (nameField && !nameHasExpr) {
						//Found that any string in double quotes were resulting in error while evaluating expression.Added this unicode replacement to fix that.
						var exp = "\"" + prefix.replace(/\"/g, "\\u0022") + "\"";

						// populate color attributes
						if (colorURL) {
							exp += "+ \"-\" + LatestRevision.TestRunColor.`Node Name`";
						}

						if (n > 1) {
							exp += "+ \"-" + i + "\"";
						}

						// use one-time expression for Name
						w.changeNode(url)
							.changeAttributeExp("Node Name", exp, true); // one time expr
					}
					// be sure to call related action for operation
					ac._onAction();
					/*For single TestRun creation, flushChanges should not be added before ChangeNode which happen at the end.
					If Node Name has one-time expression, then due to this flushChanges, expression will get executed before ChangeNode for Node Name
					and value for Node Name will not get updated as expected.*/
					if (n > 1) {
						// add a flush changes call to push data to DB
						w.flushChanges();
					}
					testRuns.push(url);
				}

			};

			var matrix = this._matrix;
			if (matrix) {
				var bColors = (dims == this.DIM_COLORS);

				// update data from editor fields.
				var matrixValues = matrix.get("value");

				matrix.rowStore.query().forEach(function(item, i) {
					var id = item.id;
					var values = matrixValues[i];

					createTestRuns(values[0], bColors ? id : null);
				}, this);
			} else {
				var quantity = Number(qty);

				if (dims == this.DIM_NONE) {
					createTestRuns(quantity, null);
				} else if (dims == this.DIM_COLORS) {
					// loop through Colors
					colors.forEach(function(colorURL) {
						createTestRuns(quantity, colorURL);
					});

					// if no test runs created, no colors were selected, so create as if DIM_NONE were the dimension
					if (!testRuns.length) {
						createTestRuns(quantity, null);
					}
				} else if (dims == this.DIM_PER_ACTIVE_COLOR) {
					// test runs for the active colors
					var cache = this.cacheView;
					_.each(cache.getNodes(this.testRunContextNode.ProductColors), function(c) {
						if (c.Active) {
							createTestRuns(quantity, c.$URL);
						}
					});
				}
			}

			// if only one test run created, then use name given rather than name constructed from color and qty
			if (testRuns.length == 1) {
				w.changeNode(testRuns[0]).changeAttribute(xml.ATTR_NAME, xml.ATTR_STRING, prefix);
			}

			if (testRuns.length) {
				_.each(testRuns, function(url) {w.validateNode(url, true);});
			}

			this.newTestRuns = testRuns;
		},

		/**
		 * @override
		 * Add product handling as necessary to fields
		 */
		_buildFields: function(fields) {
			// summary: call setup instance to update form fields and defaults based on context
			this._preProcessProduct();

			this.inherited(arguments);

			this._postProcessProduct();
		},

		/**
		 * Perform initial productSetup object and store class for future reference
		 * @return {ProductSetup} - Reference to productSetup object
		 */
		_getProductSetup: function() {
			// summary: if product is known, then return instance of product setup class
			if (!this._productSetup && this._productSetupClass) {
				var ctr = csi.syncRequire(this._productSetupClass);
				this._productSetup = new ctr();
			}
			return this._productSetup;
		},

		/**
		 * @override
		 * Implementation for getting root request in New Test Run Form
		 * @param {Node} node - parent node for request
		 * @return {PostRequest} - PostRequest object to execute
		 */
		_getRootRequest: function(node) {
			// summary: get root request for nodes needed by view - forward call to setup instance
			// as the request will differ depending on context of form
			var req = this.inherited(arguments);
			var ps = this._getProductSetup();
			var urls = [];
			var paths = [];

			if (ps && ps.runRequest) {
				ps.request(this, this.defView.getSource(), urls, paths);
			}

			if (urls.length) {
				req = req || new PostRequest(comm.MOD_SEARCH, comm.OP_URL);
				req.addParameter(comm.QUERY_URL, urls, true);
				req.addDependencyPaths(paths);
			}

			return req;
		},

		/**
		 * Handler for when Dimensions field changes
		 * @param {Widget} defView - default view for related form/field
		 * @param {Field} field - reference to field being changed
		 * @param {object} value - New value for the field
		 */
		_onChangeDimensions: function(defView, field, value) {
			// summary: render custom input checkbox based on selection
			var customQtyField = this.getField(this.CUSTOM_QUANTITY);
			var bCustomQty = customQtyField.getValue();
			var bCanCustomizeQty = (value == this.DIM_COLORS);

			this.setFieldHidden(customQtyField, !bCanCustomizeQty);

			if (bCustomQty && !bCanCustomizeQty) {
				this.setFieldValue(customQtyField, false);
			} else {
				this._onChangeCustomQuantity(defView, customQtyField, bCustomQty);
			}
		},

		/**
		 * Handler for when Dimensions field changes
		 * @param {Widget} defView - default view for related form/field
		 * @param {Field} field - reference to field being changed
		 * @param {object} value - New value for the field
		 */
		_onChangeCustomQuantity: function(defView, field, value) {
			// summary: render color fields based on selection
			//  0/2 - remove color
			//  1 - color
			// also when custom input is enabled removed color/quantity fields

			var dimensionsFieldValue = this.defView.getFieldValue(this.DIMENSIONS);
			var bHideColors = value || (dimensionsFieldValue != this.DIM_COLORS);

			this.setFieldHidden(this.colorsLabel, bHideColors);
			this.setFieldHidden(this.QUANTITY, value);

			this._removeMatrix();

			if (value) {
				this._addMatrix();
			}

			this.publishDomChange();
		},

		/**
		 * Handler for when Test Spec Group field changes
		 * @param {Widget} defView - default view for related form/field
		 * @param {Field} field - reference to field being changed
		 * @param {object} value - New value for the field
		 */
		_onChangeTestSpecGroup: function(defView, field, value) {
			this.setFieldHidden(this.GROUP_SPECS, value === csi.EMPTY_URL);
			this.setFieldHidden(this.TEST_SPECS, value !== csi.EMPTY_URL);
		},

		/**
		 * Function to add color/quantity matrix to form
		 */
		_addMatrix: function() {
			// no matrix needed for first two values
			var value = this.defView.getFieldValue(this.DIMENSIONS);
			if (value < this.DIM_COLORS) {
				return;
			}

			var bColors = (value == this.DIM_COLORS);

			this._matrixRow = this._createFieldDom();

			// form using csi-form-view-block is using display:block, so no need to create th or td
			var domNode = html.create('div', {style: 'padding-top: 6px;'}, this._matrixRow);

			var colorFieldStore = this._getColorFieldStore(this.testRunColor);

			//Declare deferred
			var dfd = colorFieldStore.query();

			//Create Memory store from field store query result
			function _createMatrixStoreFromFieldDataStore(result, store) {
				const {idProperty, labelProperty} = store;
				return new Memory({
					labelProperty: "name",
					data: csi.transform(result, function(item) {
						var id = item[idProperty];
						return (id != csi.DIALOG_URL) ? {id: id, name: item[labelProperty]} : null;
					})
				});
			}

			this.when(dfd, result => {
				// matrix should always default to 0 and not be tied to RequestTestRunQuantity field
				this._matrix = new MatrixEditor({
					defaultValue: 0,
					label: locale.resource(this.QUANTITY),
					editorArgs: {constraints: {min: 0, max: 10}},
					rowLabel: locale.resource(this.colorsLabel),
					rowStore: _createMatrixStoreFromFieldDataStore(result, colorFieldStore)
				}).placeAt(domNode);
			});
		},

		/**
		 * Function to remove color/quantity matrix from form
		 */
		_removeMatrix: function() {
			html.destroyElement(this._matrixRow);
			this._matrixRow = null;
			this._matrix = null;
		},

		/**
		 * Validate that custom quantities are within range
		 * @param {View} defView
		 * @param {Field} field
		 * @param {string} value
		 * @param {Object} params
		 */
		_onValidateCustomQuantity: function(defView, field, value, params) {
			if (this._matrix && !this._matrix.isValid()) {
				throw new Error("TestRunFormCustomQuantityInvalid");
			}
		},

		/**
		 * Handler to setup product related fields prior to init
		 */
		_preProcessProduct: function() {
			// summary: return array of possible dimension values for given product
			var ps = this._getProductSetup();
			if (!ps) {
				this._setProductDimensions([], true);
			}
			return ps ? ps.init(this, this.defView.getSource()) : null;
		},

		/**
		 * Handler to setup product related fields after init
		 */
		_postProcessProduct: function() {
			// summary: add additional fields based on product selection
			var fDim = this.defView.getField(this.DIMENSIONS);

			if (this._dimensionFields) {
				// if there is a product, set dimension fields to visible
				var bHidden = (this.testRunContextNode == null);
				for (const f of this._dimensionFields) {
					this.setFieldHidden(f, bHidden);
				}
			}

			// render dimension matrix based on current dimension value assuming dimensions is visible
			if (fDim)
				this._onChangeDimensions(this.defView, fDim, fDim.getValue());
		},

		/**
		 * @override
		 * Handle resetting product data if needed on redraw
		 */
		_redraw: function() {
			// summary: reset product if there is supplier request and product field is not fixed
			if (this.testRunContextNode && this.urlRequest && !this.getFixedValue(this.LINE_ITEM)) {
				this._resetProductData();
			}

			this.inherited(arguments);
		},

		/**
		 * Resets product data related members
		 */
		_resetProductData: function() {
			// summary: reset product members back to defaults
			this._colors = null;
			this._dimensionStore = null;
			this.testRunContextNode = null;
			this.matrixRow = null;
		},

		/**
		 * @override
		 * Handle resolving view for color/quantity handling based on product setup
		 */
		_resolveView: function() {
			if (!this.isEdit() && this.defView.getField(this.COLOR)) {
				this.defView.removeField(this.COLOR);
				colorField = null;
			}

			var colorField = null;
			if (this.hasProductColors()) {
				colorField = this.insertField(lang.mixin(new Field(), {
					m_label: this.bCanCreateMultipleTestRuns ? this.COLORS : null,
					m_id: this.COLOR,
					m_path: "Child:LatestRevision",
					m_typeControl: this.bCanCreateMultipleTestRuns ? def.FIELD_MULTI_SELECT : def.FIELD_FILTERING_SELECT
				}));
				this.colorsLabel = colorField.getControlName();
				if (xml.isValidURL(this.testRunColor)) {
					this.addFixedValue(this.COLORS, [this.testRunColor]);
				}
			}

			//if TestSpecGroup filed exists, set _onChange Methods and also add Test Specs field.
			var testSpecGroupField = this.getField(this.TEST_SPEC_GROUP);
			if (testSpecGroupField && !testSpecGroupField.bHidden) {
				testSpecGroupField.m_onChange = "_onChangeTestSpecGroup";

				var testSpecField = this.insertField(lang.mixin(new Field(), {
					m_label: this.TEST_SPECS,
					m_typeControl: def.FIELD_MULTI_SELECT
				}), 2);
				this.testSpecsLabel = testSpecField.getControlName();

				var groupSpecField = this.insertField(lang.mixin(new Field(), {
					m_label: this.GROUP_SPECS,
					widgetParams: {defaultAll: true},
					m_dependency: this.TEST_SPEC_GROUP,
					m_typeControl: def.FIELD_MULTI_SELECT,
					m_class: html.CSS_HIDE,
					m_relationship: lang.mixin(new Relationship(), {
						m_id: "TestSpecs",
						m_query: new Query()
							.Attr("State", xml.OP_EQUALS, "Revision.State:APPROVED", "Child:CurrentRevision")
					})
				}), 2);
			}

			// add fields that may be needed when creating new Test Runs - not for Copy or Edit use cases
			if (this.bCanCreateMultipleTestRuns) {
				var a = this._dimensionFields = [];
				if (colorField) {
					a.push(colorField);
				}
				if (ProductType.hasColor(this.testRunContextNode)) {
					this.insertField(lang.mixin(new Field(), {
						m_label: this.DIMENSIONS,
						m_onChange: "_onChangeDimensions",
						m_typeControl: def.FIELD_FILTERING_SELECT
					}), this.colorsLabel);

					a.push(this.insertField(lang.mixin(new Field(), {
						m_label: this.CUSTOM_QUANTITY,
						m_typeControl: def.FIELD_CHECKBOX,
						m_onChange: "_onChangeCustomQuantity",
						m_onValidate: "_onValidateCustomQuantity",
						bCombineWithPrevious: true,
						bHideLabelForCombinedField: false
					}), this.colorsLabel));
				}

				// if valid TestRun color is specified, force dimension to colors
				if (xml.isValidURL(this.testRunColor)) {
					this.addFixedValue(this.DIMENSIONS, [this.DIM_COLORS]);
				} else if (this.testRunColor) {
					// expected to enter this branch when testRunColor == "centric:", which signifies
					// that no color should be selectable, even if colors are available.  If testRunColor == null,
					// color should be selectable.
					this.addFixedValue(this.DIMENSIONS, [this.DIM_NONE]);
				}

				a.push(this.insertField(lang.mixin(new Field(), {
					m_label: this.QUANTITY,
					m_type: xml.ATTR_INTEGER,
					widgetParams: {constraints: {min: 1, max: 10}}
				})));

				this.addDefaultValue(this.QUANTITY, 1);
			}

			this.inherited(arguments);
		},

		/**
		 * Set available product dimensions based on the product setup
		 * @param {Array} colors - string Array of color URLs
		 * @param {Boolean} bNone - flag specifying if the "None" option should be available
		 * @return {Memory} - Memory datastore to be used for list on Dimensions pulldown
		 */
		_setProductDimensions: function(colors, bNone) {
			var fDim = this.defView.getField(this.DIMENSIONS);
			this._colors = xml.isValidURL(this.testRunColor) ? [this.testRunColor] : colors;
			var nColors = this._colors ? this._colors.length : 0;

			// nothing more to do if no field to show dimensions
			if (!fDim) {
				return null;
			}

			// populate possible drop-down values for dimensions
			var items = [];

			if (bNone && !xml.isValidURL(this.testRunColor)) {
				items.push({name: locale.resource("None"), id: this.DIM_NONE});
			}

			if (nColors && bNone) { // bNone is disallowed on SizeChartReview and there Colors option allow is disallowed too
				items.push({name: locale.resource("DimensionColors"), id: this.DIM_COLORS});
			}

			// bNone is disallowed on SizeChartReview and there Colors option allow is disallowed too, so disable Test Run per active Color
			if (!this.testRunContextNode || (this.testRunContextNode.$Type != "TestRun" && nColors && bNone && !this.testRunColor)) {
				items.push({name: locale.resource("DimensionActiveColors", [locale.type("TestRun")]), id: this.DIM_PER_ACTIVE_COLOR});
			}

			this._dimensionStore = new Memory({data: items});

			return _.map(items, function(obj) {return obj.id;});
		},

		_shouldInsertRevisionBehaviorField: function() {
			// Summary: Don't show the Revision Behavior field on copying TestRun Template
			if (this.getViewNode() && this.getViewNode().__Parent__ == "centric://QUALITY/QualityAdmin")
				return false;

			return this.inherited(arguments);
		}
	});
});
